/**
 * PiecesLostScoreboard Component
 *
 * Compact scoreboard displaying piece loss counts for both players.
 * Positioned above player grids for easy reference during gameplay.
 *
 * Features:
 * - Real-time piece loss tracking
 * - Color-coded by player (cyan for you, pink for opponent)
 * - Supports bonus piece (4 vs 5 total pieces)
 * - Danger indicator when close to defeat (3+/4 or 4+/5 pieces lost)
 * - Compact single-line design
 * - Responsive layout
 * - Bonus piece captured badge display (Phase 7.9.4.1)
 *
 * @param {number} myPiecesLost - Number of pieces I've lost (0-4 or 0-5)
 * @param {number} myTotalPieces - Total pieces I have (4 or 5 with bonus)
 * @param {number} opponentPiecesLost - Number of pieces opponent lost (0-4 or 0-5)
 * @param {number} opponentTotalPieces - Total pieces opponent has (4 or 5 with bonus)
 * @param {boolean} myHasCapturedBonus - Whether I captured the bonus piece (Phase 7.9.4.1)
 * @param {boolean} opponentHasCapturedBonus - Whether opponent captured bonus piece (Phase 7.9.4.1)
 */

import { COPY } from '../copy';
import BonusCapturedBadge from './BonusCapturedBadge';

export default function PiecesLostScoreboard({
  myPiecesLost = 0,
  myTotalPieces = 4,
  opponentPiecesLost = 0,
  opponentTotalPieces = 4,
  myHasCapturedBonus = false,
  opponentHasCapturedBonus = false,
}) {
  // Sanitize input: ensure non-negative and cap at total
  const sanitizedMyLost = Math.max(0, Math.min(myPiecesLost, myTotalPieces));
  const sanitizedOpponentLost = Math.max(
    0,
    Math.min(opponentPiecesLost, opponentTotalPieces)
  );

  // Determine danger state (3+ out of 4, or 4+ out of 5)
  const myDanger = sanitizedMyLost >= myTotalPieces - 1;
  const opponentDanger = sanitizedOpponentLost >= opponentTotalPieces - 1;

  return (
    <div
      data-testid="pieces-lost-scoreboard"
      className="flex justify-between items-center gap-4 text-sm px-2 py-1"
    >
      {/* My Pieces Lost */}
      <div
        data-testid="my-pieces-lost"
        className={`flex items-center gap-2 ${
          myDanger ? 'text-red-400 font-bold' : 'text-cyan-400'
        }`}
      >
        <span className="text-xs opacity-80">{COPY.scoreboard.you}</span>
        <span data-testid="my-pieces-count" className="font-mono font-semibold">
          {COPY.scoreboard.piecesLost(sanitizedMyLost, myTotalPieces)}
        </span>
        <BonusCapturedBadge hasCapturedBonus={myHasCapturedBonus} />
      </div>

      {/* Opponent Pieces Lost */}
      <div
        data-testid="opponent-pieces-lost"
        className={`flex items-center gap-2 ${
          opponentDanger ? 'text-red-400 font-bold' : 'text-pink-400'
        }`}
      >
        <span className="text-xs opacity-80">{COPY.scoreboard.opponent}</span>
        <span
          data-testid="opponent-pieces-count"
          className="font-mono font-semibold"
        >
          {COPY.scoreboard.piecesLost(sanitizedOpponentLost, opponentTotalPieces)}
        </span>
        <BonusCapturedBadge hasCapturedBonus={opponentHasCapturedBonus} />
      </div>
    </div>
  );
}
